import { useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import StudentLayout from "@/components/StudentLayout";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Button } from "@/components/ui/button";
import { Video, ExternalLink, Lock, CheckCircle2, Clock3 } from "lucide-react";
import { toast } from "sonner";
import JitsiRoomOverlay from "@/components/JitsiRoomOverlay";
import DailyRoomOverlay from "@/components/DailyRoomOverlay";
import { to12h } from "@/lib/time";

function todayISO() {
  const d = new Date();
  const tz = d.getTimezoneOffset();
  return new Date(d.getTime() - tz * 60_000).toISOString().slice(0, 10);
}

function nowHM() {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}

function parseHM(s) {
  const [h, m] = (s || "0:0").split(":").map((x) => parseInt(x, 10) || 0);
  return h * 60 + m;
}

function statusOf(cls, isToday, now) {
  if (!isToday) {
    // For non-today views, decide by date relative to today
    const t = todayISO();
    if (cls.date < t) return "ended";
    if (cls.date > t) return "upcoming";
  }
  const s = parseHM(cls.start_time);
  const e = parseHM(cls.end_time);
  if (now < s) return "upcoming";
  if (now >= s && now <= e) return "live";
  return "ended";
}

export default function StudentOnlineClasses() {
  const [tab, setTab] = useState("today"); // today | upcoming | past | date
  const [date, setDate] = useState(todayISO());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(nowHM());
  const [studentName, setStudentName] = useState("");
  const [activeRoom, setActiveRoom] = useState(null); // { room, subject } — Jitsi (legacy)
  const [dailyRoom, setDailyRoom] = useState(null); // { roomUrl, token, subject }
  const [joining, setJoining] = useState(null); // class id being joined

  useEffect(() => {
    api.get("/student/me").then(({ data }) => setStudentName(data?.name || "")).catch(() => {});
  }, []);

  const joinDaily = async (cls) => {
    setJoining(cls.id);
    try {
      const { data } = await api.post(`/student/online-classes/${cls.id}/daily-token`);
      setDailyRoom({ roomUrl: data.room_url, token: data.token, subject: cls.subject });
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not join the class");
    } finally { setJoining(null); }
  };

  // Tick every 20s so live/upcoming transitions happen without reload
  useEffect(() => {
    const t = setInterval(() => setNow(nowHM()), 20_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const params = tab === "date" ? { date } : { range: tab };
        const { data } = await api.get("/student/online-classes", { params });
        if (!cancelled) setItems(data.items || []);
      } catch (e) {
        if (!cancelled) toast.error(apiErr(e.response?.data?.detail) || "Could not load classes");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [tab, date]);

  const isToday = tab === "today" || (tab === "date" && date === todayISO());
  const rows = useMemo(() => items.map((c) => ({ ...c, _status: statusOf(c, isToday, now) })), [items, isToday, now]);

  const tabs = [
    { id: "today", label: "Today" },
    { id: "upcoming", label: "Upcoming" },
    { id: "past", label: "Past" },
    { id: "date", label: "By date" },
  ];

  return (
    <StudentLayout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Online classes</div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>
            {tab === "past" ? "Past online classes" : tab === "upcoming" ? "Upcoming online classes" : "Today's online classes"}
          </h1>
          <p className="text-slate-500 mt-1">
            {tab === "past"
              ? "Sessions that already happened — links may be expired."
              : tab === "upcoming"
                ? "Scheduled for a future date. Join opens on the day."
                : "Join live classes when they start. Past and upcoming sessions are locked."}
          </p>
        </div>
        {tab === "date" && (
          <DatePicker value={date} onChange={setDate} className="h-11 rounded-xl w-48" data-testid="oc-date"/>
        )}
      </div>

      <div className="inline-flex gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-800 mb-5" data-testid="oc-tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            data-testid={`oc-tab-${t.id}`}
            className={`text-xs font-semibold px-3.5 py-2 rounded-lg transition-colors ${
              tab === t.id
                ? "bg-white dark:bg-slate-900 text-sky-600 shadow-sm"
                : "text-slate-600 dark:text-slate-300 hover:text-slate-900"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-10 text-center text-slate-500">Loading…</div>
      ) : rows.length === 0 ? (
        <NoClassesCard date={tab === "date" ? date : tab} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="oc-list">
          {rows.map((c) => <ClassCard key={c.id} cls={c} joining={joining} onJoinJitsi={(cls) => setActiveRoom({ room: cls.room_name, subject: cls.subject })} onJoinDaily={joinDaily} />)}
        </div>
      )}
      {activeRoom && (
        <JitsiRoomOverlay
          room={activeRoom.room}
          subject={activeRoom.subject}
          displayName={studentName || "Student"}
          isHost={false}
          onClose={() => setActiveRoom(null)}
        />
      )}
      {dailyRoom && (
        <DailyRoomOverlay
          roomUrl={dailyRoom.roomUrl}
          token={dailyRoom.token}
          subject={dailyRoom.subject}
          isHost={false}
          onClose={() => setDailyRoom(null)}
        />
      )}
    </StudentLayout>
  );
}

function ClassCard({ cls, onJoinJitsi, onJoinDaily, joining }) {
  const s = cls._status;
  const tone = s === "live"
    ? "border-emerald-300 dark:border-emerald-700 bg-emerald-50/60 dark:bg-emerald-950/30"
    : s === "upcoming"
      ? "border-sky-200 dark:border-sky-800 bg-white dark:bg-slate-800"
      : "border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 opacity-75";
  const badge = s === "live"
    ? { tone: "bg-emerald-500 text-white", text: "● LIVE NOW" }
    : s === "upcoming"
      ? { tone: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300", text: `Starts ${to12h(cls.start_time)}` }
      : s === "ended"
        ? { tone: "bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400", text: "Ended" }
        : { tone: "bg-slate-100 text-slate-500", text: to12h(cls.start_time) };

  return (
    <div className={`rounded-2xl border p-5 transition-all ${tone}`} data-testid={`oc-card-${cls.id}`} data-status={s}>
      <div className="flex items-center justify-between mb-3">
        <div className="h-9 w-9 rounded-xl bg-sky-500 text-white flex items-center justify-center">
          <Video className="h-4 w-4" />
        </div>
        <div className="flex items-center gap-1.5">
          {cls.provider === "zoho" && (
            <span className="text-[9px] font-semibold uppercase tracking-wider bg-emerald-500 text-white px-1.5 py-0.5 rounded" data-testid={`oc-zoho-${cls.id}`}>Zoho</span>
          )}
          {(cls.provider === "jitsi" || cls.provider === "daily") && (
            <span className="text-[9px] font-semibold uppercase tracking-wider bg-indigo-500 text-white px-1.5 py-0.5 rounded" data-testid={`oc-jitsi-${cls.id}`}>In-app</span>
          )}
          <span className={`text-[10px] font-semibold uppercase tracking-[0.15em] px-2 py-1 rounded-full ${badge.tone}`} data-testid={`oc-badge-${cls.id}`}>
            {badge.text}
          </span>
        </div>
      </div>
      <div className="text-base font-semibold text-slate-900 dark:text-slate-100 leading-tight" style={{fontFamily:"Outfit"}}>{cls.subject}</div>
      <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
        <Clock3 className="h-3 w-3 inline mr-1 -mt-0.5" />
        {cls.date && cls.date !== todayISO() ? <>{cls.date} · </> : null}
        {cls.start_time && cls.end_time ? `${to12h(cls.start_time)} – ${to12h(cls.end_time)}` : null}
        {cls.period ? <> · Period {cls.period}</> : null}
      </div>
      {cls.description && <p className="text-sm text-slate-600 dark:text-slate-300 mt-3 line-clamp-3">{cls.description}</p>}

      <div className="mt-4">
        {s === "live" ? (
          cls.provider === "daily" ? (
            <Button onClick={() => onJoinDaily(cls)} disabled={joining === cls.id} className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid={`oc-join-${cls.id}`}>
              <Video className="h-4 w-4 mr-1" /> {joining === cls.id ? "Joining…" : "Join in-app"}
            </Button>
          ) : cls.provider === "jitsi" ? (
            <Button onClick={() => onJoinJitsi(cls)} className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid={`oc-join-${cls.id}`}>
              <Video className="h-4 w-4 mr-1" /> Join in-app
            </Button>
          ) : (
            <Button asChild className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid={`oc-join-${cls.id}`}>
              <a href={cls.meeting_url} target="_blank" rel="noreferrer">
                <ExternalLink className="h-4 w-4 mr-1" /> Join class
              </a>
            </Button>
          )
        ) : s === "upcoming" ? (
          <Button disabled className="w-full rounded-full" data-testid={`oc-locked-${cls.id}`}>
            <Lock className="h-4 w-4 mr-1" /> Opens at {to12h(cls.start_time)}
          </Button>
        ) : s === "ended" ? (
          <Button disabled variant="outline" className="w-full rounded-full" data-testid={`oc-ended-${cls.id}`}>
            <CheckCircle2 className="h-4 w-4 mr-1" /> Class ended
          </Button>
        ) : (
          <Button disabled variant="outline" className="w-full rounded-full">
            {to12h(cls.start_time)} – {to12h(cls.end_time)}
          </Button>
        )}
      </div>
    </div>
  );
}

function NoClassesCard({ date }) {
  const greetings = [
    { headline: "Heyyyyy… no online classes today!!!", sub: "Take a stroll, read a book, or call a friend." },
    { headline: "Screen-free day 🌿", sub: "No online classes scheduled. Enjoy the offline world." },
    { headline: "Class-free pass!", sub: "Nothing online today. Treat yourself." },
  ];
  const pick = greetings[Math.floor(Math.random() * greetings.length)];
  const dots = Array.from({ length: 24 }).map((_, i) => i);
  return (
    <div className="relative overflow-hidden rounded-3xl border border-sky-200 dark:border-sky-800 bg-gradient-to-br from-sky-50 via-violet-50 to-pink-50 dark:from-sky-950/40 dark:via-violet-950/30 dark:to-pink-950/30 p-10 sm:p-16 text-center" data-testid="oc-empty">
      <style>{`
        @keyframes confetti-fall { 0% { transform: translateY(-110%) rotate(0deg); opacity: 0; } 10% { opacity: 1; } 100% { transform: translateY(110vh) rotate(720deg); opacity: 0.9; } }
        @keyframes wiggle { 0%, 100% { transform: rotate(-6deg); } 50% { transform: rotate(6deg); } }
      `}</style>
      <div className="absolute inset-0 pointer-events-none">
        {dots.map((i) => {
          const colors = ["#f43f5e", "#f59e0b", "#22c55e", "#0ea5e9", "#a855f7"];
          const left = (i * 137) % 100;
          const delay = (i % 7) * 0.5;
          const dur = 3 + (i % 5);
          const size = 6 + (i % 4) * 2;
          return <span key={i} style={{ position: "absolute", left: `${left}%`, top: 0, width: size, height: size * 1.6, background: colors[i % colors.length], borderRadius: 2, animation: `confetti-fall ${dur}s linear ${delay}s infinite` }}/>;
        })}
      </div>
      <div className="relative">
        <div className="mx-auto h-20 w-20 rounded-3xl bg-white dark:bg-slate-800 shadow-lg flex items-center justify-center text-5xl mb-6" style={{ animation: "wiggle 1.4s ease-in-out infinite" }}>🌴</div>
        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100 mb-3" style={{ fontFamily: "Caveat, Outfit, cursive" }}>
          {pick.headline}
        </h2>
        <p className="text-base text-slate-600 dark:text-slate-300 max-w-md mx-auto mb-5">{pick.sub}</p>
        <div className="inline-flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 bg-white/70 dark:bg-slate-900/40 backdrop-blur px-3 py-1.5 rounded-full">
          <Video className="h-3.5 w-3.5" /> Nothing scheduled for <b>{date}</b>
        </div>
      </div>
    </div>
  );
}
